// src/features/availability/types/schemas.ts
import { z } from 'zod';

import {
  LANGUAGE_KEYS,
  PRACTITIONER_CONCERN_KEYS,
  PRACTITIONER_SPECIALIZATION_KEYS,
} from '../constants/practitionerFilterEnums';
import { END_OF_DAY } from '../lib/time';

/**
 * Mirrors elixaa-core/elixaa-api/src/schemas/availabilitySchema.ts.
 * Cross-repo mirroring is intentional — the FE accepts the maintenance cost
 * to keep itself deployable without the BE repo on disk.
 */

const startTimeRegex = /^([01]\d|2[0-3]):(00|15|30|45)$/;
const endTimeRegex = /^(([01]\d|2[0-3]):(00|15|30|45)|24:00)$/;
const ID_MAX = 50;

/** YYYY-MM-DD with calendar-validity round-trip. Mirrors `calendarDateString`. */
const calendarDateString = (label = 'Date') =>
  z
    .string()
    .regex(/^[12]\d{3}-\d{2}-\d{2}$/, `${label} must be YYYY-MM-DD`)
    .refine(
      (s) => {
        const parts = s.split('-');
        const y = parts[0] ? Number(parts[0]) : undefined;
        const m = parts[1] ? Number(parts[1]) : undefined;
        const d = parts[2] ? Number(parts[2]) : undefined;
        if (y === undefined || m === undefined || d === undefined) return false;
        const dt = new Date(Date.UTC(y, m - 1, d));
        return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
      },
      { message: `${label} must be a valid calendar date` },
    );

export const modeEnum = z.enum(['online', 'offline', 'both']);
// Mirrors PROJECT_KEYS in @/lib/projects (which mirrors BE
// elixaa-core/packages/db/src/schema/projects.ts). Duplicated as a literal
// rather than imported so this schema stays self-contained per the file
// header note. Keep in sync — projects.test.ts asserts the same order.
export const projectKeyEnum = z.enum(['campus_support', 'helpline_support', 'portal_helpline']);

export type Mode = z.infer<typeof modeEnum>;
export type ProjectKey = z.infer<typeof projectKeyEnum>;

const startTimeSchema = z
  .string()
  .regex(startTimeRegex, 'Start time must be HH:MM aligned to :00/:15/:30/:45');
const endTimeSchema = z
  .string()
  .regex(endTimeRegex, 'End time must be HH:MM (15-min aligned) or 24:00');

/** Body shape for both working blocks (mode set) and off markers (mode null). */
export const blockBodySchema = z
  .object({
    date: calendarDateString('Date'),
    startTime: startTimeSchema,
    endTime: endTimeSchema,
    mode: modeEnum.nullable().optional(),
    reason: z.string().max(255).nullable().optional(),
  })
  .refine((b) => b.endTime === END_OF_DAY || b.endTime > b.startTime, {
    message: 'End time must be after start time',
    path: ['endTime'],
  });

export type BlockBody = z.infer<typeof blockBodySchema>;

export const assignmentBodySchema = z
  .object({
    startTime: startTimeSchema,
    endTime: endTimeSchema,
    projectKey: projectKeyEnum,
    locationIds: z
      .array(z.string().max(ID_MAX))
      .min(1, 'At least one location is required')
      .max(50)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: 'locationIds must be unique',
      }),
  })
  .refine((b) => b.endTime === END_OF_DAY || b.endTime > b.startTime, {
    message: 'End time must be after start time',
    path: ['endTime'],
  });

export type AssignmentBody = z.infer<typeof assignmentBodySchema>;

export const listAvailabilityQuerySchema = z
  .object({
    from: calendarDateString('From date').optional(),
    to: calendarDateString('To date').optional(),
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: "'from' must be on or before 'to'",
    path: ['from'],
  });

export type ListAvailabilityQuery = z.infer<typeof listAvailabilityQuerySchema>;

export const searchAvailabilitySchema = z
  .object({
    date: calendarDateString('Date'),
    fromTime: z.string().regex(startTimeRegex).optional(),
    toTime: z.string().regex(endTimeRegex).optional(),
    mode: z.enum(['online', 'offline']).optional(),
    /** Phase 2 — therapy modalities filter (BE accepts string[] since 988adf8). */
    specialization: z
      .union([z.string(), z.array(z.string())])
      .transform((v) => (Array.isArray(v) ? v : [v]))
      .pipe(z.array(z.enum(PRACTITIONER_SPECIALIZATION_KEYS)))
      .optional(),
    /** Phase 2 — concerns the practitioner helps with (BE accepts since 988adf8). */
    concerns: z
      .union([z.string(), z.array(z.string())])
      .transform((v) => (Array.isArray(v) ? v : [v]))
      .pipe(z.array(z.enum(PRACTITIONER_CONCERN_KEYS)))
      .optional(),
    /** Phase 2 — practitioner spoken languages (BE accepts since 988adf8). */
    languages: z
      .union([z.string(), z.array(z.string())])
      .transform((v) => (Array.isArray(v) ? v : [v]))
      .pipe(z.array(z.enum(LANGUAGE_KEYS)))
      .optional(),
  })
  .refine((q) => !q.fromTime || !q.toTime || q.fromTime < q.toTime, {
    message: "'fromTime' must be before 'toTime'",
    path: ['fromTime'],
  });

export type SearchAvailabilityQuery = z.infer<typeof searchAvailabilitySchema>;

/**
 * Assignment row. Used by both the standalone assignment endpoints
 * (POST/PATCH/GET on `/blocks/:blockId/assignments`) and the embedded
 * shape returned inside `GET /practitioners/:id/availability` rows.
 *
 * The embedded variant omits `blockId` (redundant — the parent block's id
 * is implicit) and `updatedAt`. We mark both fields nullish to accept
 * both shapes against a single schema. Mirrors elixaa-core's
 * `availabilityService.ts:357-366` (embedded) vs
 * `assignmentService.ts:244-264` (standalone formatAssignment).
 */
export const blockAssignmentRowSchema = z.object({
  id: z.string(),
  blockId: z.string().nullish(),
  startTime: z.string(),
  endTime: z.string(),
  projectKey: projectKeyEnum,
  locationIds: z.array(z.string()),
  assignedBy: z.string(),
  updatedAt: z.string().nullish(),
});

export type BlockAssignmentRow = z.infer<typeof blockAssignmentRowSchema>;

export const availabilityRowSchema = z.object({
  id: z.string(),
  practitionerId: z.string(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  mode: modeEnum.nullable(),
  reason: z.string().nullable(),
  changedBy: z.string().nullable(),
  updatedAt: z.string().nullable(),
  /**
   * Assignments are present on `GET /practitioners/:id/availability` (the
   * range endpoint decorates each block with its current assignments), but
   * absent on `POST /availability/blocks` and `PATCH /availability/blocks/:id`
   * (those endpoints return the raw DB row, no join). Default to an empty
   * array so a single shared schema parses both shapes.
   * Mirrors elixaa-core/elixaa-api/src/controllers/availabilityController.ts:62
   * (updateBlock — returns `block` unjoined) vs availabilityService.ts:357-366
   * (listRange — embeds assignments).
   */
  assignments: z.array(blockAssignmentRowSchema).optional().default([]),
});

export type AvailabilityRow = z.infer<typeof availabilityRowSchema>;

export const availabilityRangeResponseSchema = z.object({
  from: z.string(),
  to: z.string(),
  rows: z.array(availabilityRowSchema),
});

export type AvailabilityRangeResponse = z.infer<typeof availabilityRangeResponseSchema>;

/** Response row from `GET /practitioners/availability/search`. */
export const searchSlotSchema = z.object({
  blockId: z.string(),
  assignmentId: z.string(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  mode: modeEnum,
});

export const searchPractitionerSchema = z.object({
  practitioner: z.object({
    id: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    /** Volunteers come with a single `displayName` (e.g. "Campus Buddy") and
     *  null first/last name. Psychologists provide first/last; displayName is
     *  optional. Card UI prefers displayName when present. */
    displayName: z.string().nullable().optional(),
    profilePicture: z.string().nullable(),
    /** Phase 2 — therapy modalities. Now typed against the BE enum. */
    specializations: z.array(z.enum(PRACTITIONER_SPECIALIZATION_KEYS)).default([]),
    /** Phase 2 — what the practitioner helps clients with. */
    concerns: z.array(z.enum(PRACTITIONER_CONCERN_KEYS)).default([]),
    /** Phase 2 — spoken languages. */
    languages: z.array(z.enum(LANGUAGE_KEYS)).default([]),
    qualification: z.string().nullable(),
    experienceYears: z.number().nullable(),
    /** Phase 2 — short bio displayed on the expanded practitioner card. */
    bio: z.string().nullable().optional(),
    /** Practitioner gender ('male' | 'female' | 'other' | 'unknown'), shown as a
     *  pill in the View Profile dialog. Optional for forward/backward compat. */
    gender: z.string().nullable().optional(),
    /** Phase 2 — coarse role for FE rendering ('Volunteer' vs 'Psychologist'
     *  chip). chief_psychologist collapses to 'psychologist' so org hierarchy
     *  isn't leaked to clients. Mirrors sessionService output (commit 2134c34). */
    practitionerKind: z.enum(['volunteer', 'psychologist']).optional(),
  }),
  slots: z.array(searchSlotSchema),
});

export const searchAvailabilityResponseSchema = z.object({
  date: z.string(),
  results: z.array(searchPractitionerSchema),
});

export type SearchAvailabilityResponse = z.infer<typeof searchAvailabilityResponseSchema>;
export type SearchSlot = z.infer<typeof searchSlotSchema>;
export type SearchPractitionerResult = z.infer<typeof searchPractitionerSchema>;

/** Row from `GET /practitioners/availability/roster` (and the volunteer twin):
 *  every practitioner the student can book at their location, with their free
 *  slots across the whole booking window. `slots` is empty for anyone with no
 *  opening this week; they are still listed. */
export const bookingRosterEntrySchema = searchPractitionerSchema.extend({
  /** When this student last completed a session with the practitioner (ISO),
   *  null if never. */
  lastSessionAt: z.string().nullish(),
});

export const bookingRosterResponseSchema = z.object({
  /** First and last day of the booking window, YYYY-MM-DD, BA timezone. */
  from: z.string(),
  to: z.string(),
  results: z.array(bookingRosterEntrySchema),
});

export type BookingRosterEntry = z.infer<typeof bookingRosterEntrySchema>;
export type BookingRosterResponse = z.infer<typeof bookingRosterResponseSchema>;
